/**
 * Social-video generation SSE pipeline → store subscriber.
 * Migrated in Phase 8 commit 79.
 */

import { sseEvents } from "./sseEvents";
import type { CampaignData, CoAgentMessage } from "../store/department/types";

interface SocialVideoSubscriberHooks {
  updateCampaignData: (data: Partial<CampaignData>) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  getCampaignData: () => CampaignData;
}

let wired = false;

export function wireSocialVideoSubscriber(
  getHooks: () => SocialVideoSubscriberHooks,
): void {
  if (wired) return;
  wired = true;

  sseEvents.subscribe("social-video:input-validated", () => {
    getHooks().updateCampaignData({
      socialVideoGenerationState: {
        status: "running",
        phase: "input_validated",
        progressMessage: "Input validated, preparing generation...",
      },
    });
  });

  sseEvents.subscribe("social-video:prompt-compiled", () => {
    getHooks().updateCampaignData({
      socialVideoGenerationState: {
        status: "running",
        phase: "prompt_compiled",
        progressMessage: "Prompt compiled, starting video generation...",
      },
    });
  });

  sseEvents.subscribe("social-video:video-generation-running", ({ message }) => {
    getHooks().updateCampaignData({
      socialVideoGenerationState: {
        status: "running",
        phase: "video_generation",
        progressMessage: message,
      },
    });
  });

  sseEvents.subscribe("social-video:video-download", () => {
    getHooks().updateCampaignData({
      socialVideoGenerationState: {
        status: "running",
        phase: "video_download",
        progressMessage: "Downloading and uploading to storage...",
      },
    });
  });

  sseEvents.subscribe("social-video:done", ({ video }) => {
    const hooks = getHooks();
    const existing = hooks.getCampaignData().socialGeneratedVideos ?? [];
    hooks.updateCampaignData({
      socialVideoGenerationState: {
        status: "complete",
        phase: "done",
        progressMessage: "Video ready!",
      },
      socialGeneratedVideos: [...existing, video],
    });
    hooks.addMessage({
      role: "agent",
      content: `Video generated successfully! "${video.title}" is ready to download or send to production.`,
      widget: "videoProductionQueue",
    });
  });

  sseEvents.subscribe("social-video:error", ({ message }) => {
    getHooks().updateCampaignData({
      socialVideoGenerationState: {
        status: "error",
        phase: "error",
        error: message,
      },
    });
  });
}
